import React, { createContext, useContext, useState, ReactNode } from 'react';
import { getCurrentUser, signIn, signUp, signOut, confirmResetPassword, updateUserAttributes} from 'aws-amplify/auth';
import axios from 'axios';
import baseUrl from '../Utils/baseUrl';
import { Navigate, redirect, useNavigate } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode, resetPassword } from 'aws-amplify/auth'


const accessOptions = {
  'PHG620I': {
    'code': 'PHG620I',
    'company': 'PHG',
    'privileges': 'staff',
    'department': 'intake'
  },
  'PHG620MI': {
    'code': 'PHG620MI',
    'company': 'PHG',
    'privileges': 'manager',
    'department': 'intake'
  },
  'PHG620BD': {
    'code': 'PHG620BD',
    'company': 'PHG',
    'privileges': 'staff',
    'department': 'bd'
  },
  'PHG620B': {
    'code': 'PHG620B',
    'company': 'PHG',
    'privileges': 'staff',
    'department': 'billing'
  },
  'PHG620A': {
    'code': 'PHG620A',
    'company': 'PHG',
    'privileges': 'admin',
    'department': 'admin'
  },
  'PHG620D': {
    'code': 'PHG620D',
    'company': 'PHG',
    'privileges': 'dev',
    'department': 'dev'
  },
  'PHG620C': {
    'code': 'PHG620C',
    'company': 'PHG',
    'privileges': 'staff',
    'department': 'claims'
  },
  'PHG620F': {
    'code': 'PHG620F',
    'company': 'PHG',
    'privileges': 'staff',
    'department': 'followup'
  },
}

interface accessCode {
  code: string,
  company: string,
  privileges: string,
  department: string
}

interface UserPassthrough {
  firstName: string,
  lastName: string,
  email: string,
  password: string
}

interface UserDetails {
  username: string,
  password: string
}

interface Account {
  userId: string | null | undefined;
  username: string | null | undefined;
}

interface ProfileProps {
  active: boolean;
  company: string;
  department: string;
  email: string;
  first_name: string;
  last_name: string;
  name: string;
  privileges: string;
  userid: string;
  profile_photo?: string | null;
}

interface Profile {
  company: string | null;
  creation_date: string | null;
  department: string | null;
  email: string | null;
  last_login: string | null;
  first_name: string | null;
  last_name: string | null;
  name: string | null;
  privileges: string | null;
  userid: string | null;
  profile_photo?: string | null;
  timezone?: string | null;
}

interface AuthContextType {
  currentUser: Account;
  currentProfile: Profile;
  authLoading: boolean;
  validLogin: boolean;
  validAccessCode: boolean;
  invalidEmail: boolean;
  confirmedEmail: boolean;
  createdAccount: boolean;
  accessCode: accessCode;
  successfulReset: boolean;
  resetError: string;
  email: string,
  allUsers: ProfileProps[] | null,
  intakeUsers: ProfileProps[] | null,
  adminUsers: ProfileProps[] | null,
  claimsUsers: ProfileProps[] | null,
  followupUsers: ProfileProps[] | null,
  devUsers: ProfileProps[] | null,
  signInUser: ({username, password}: UserDetails) => void;
  validateAccessCode: (accessCode: string) => void;
  removeAccessCode: () => void;
  createNewUser: ({
    firstName,
    lastName,
    email,
    password
  }: UserPassthrough) => void;
  grabCurrentUser: () => void;
  unvalidateAccessCode: () => void;
  signOutUser: () => void;
  resetConfirmEmail: () => void;
  confirmEmailCode: (confirmationCode: string) => void;
  grabCurrentUserProfile: (userId: string) => void;
  resetUserEmail: (email: string) => void;
  confirmResetPasswrd: (
    confirmationCode: string,
    newPassword: string,
  ) => void;
  handleEmailUpdate: (data: string) => void,
  grabAllProfiles: () => void
}

const AuthContext = createContext<AuthContextType>({
  currentUser: { userId: null, username: null },
  currentProfile: {
    company: null,
    creation_date: null,
    department: null,
    email: null,
    last_login: null,
    first_name: null,
    last_name: null,
    name: null,
    privileges: null,
    userid: null,
  },
  authLoading: false,
  validLogin: true,
  validAccessCode: false,
  confirmedEmail: false,
  createdAccount: false,
  invalidEmail: false,
  accessCode: {code: '', company: '', privileges: '', department: ''},
  successfulReset: true,
  resetError: '',
  email: '',
  allUsers: [],
  intakeUsers: [],
  adminUsers: [],
  claimsUsers: [],
  followupUsers: [],
  devUsers: [],
  confirmEmailCode: () => {},
  unvalidateAccessCode: () => {},
  signInUser: () => {},
  createNewUser: () => {},
  removeAccessCode: () => {},
  signOutUser: () => {},
  resetConfirmEmail: () => {},
  validateAccessCode: () => {},
  grabCurrentUser: () => {},
  grabCurrentUserProfile: () => {},
  resetUserEmail: () => {},
  confirmResetPasswrd: () => {},
  handleEmailUpdate: () => {},
  grabAllProfiles: () => {}
});

interface AuthProviderProps {
  children: ReactNode;
}

export function useAuth() {
  return useContext(AuthContext);
}

// Global axios interceptor to send user ID on every request for IP firewall
axios.interceptors.request.use((config) => {
  const userId = localStorage.getItem('intellasure_user_id');
  if (userId) {
    config.headers['X-User-Id'] = userId;
  }
  return config;
});

// Catch 403 firewall blocks and notify the app
// Only trigger if X-User-Id was sent (meaning the firewall had user context and still blocked)
// Also detect sustained backend unavailability (network errors, 5xx) and flip maintenance screen.
let consecutiveBackendFailures = 0;
let backendDownNotified = false;
let lastFailureAt = 0;
const FAILURE_THRESHOLD = 3;
const FAILURE_DECAY_MS = 30_000;

// Recognize axios/browser cancellations and aborts. These happen normally
// during auth transitions (sign-out navigates away, in-flight requests get
// cancelled) and on form re-renders (autofill changing inputs mid-request),
// and must NOT be counted as backend failures.
function isCancellation(error: any): boolean {
  if (axios.isCancel?.(error)) return true;
  if (error?.code === 'ERR_CANCELED') return true;
  if (error?.name === 'CanceledError' || error?.name === 'AbortError') return true;
  if (error?.message === 'canceled') return true;
  return false;
}

axios.interceptors.response.use(
  (response) => {
    if (backendDownNotified) {
      backendDownNotified = false;
      window.dispatchEvent(new CustomEvent('backend-available'));
    }
    consecutiveBackendFailures = 0;
    return response;
  },
  (error) => {
    if (isCancellation(error)) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === 403 &&
      error.response?.data?.error === 'Access denied' &&
      error.config?.headers?.['X-User-Id']
    ) {
      window.dispatchEvent(new CustomEvent('ip-blocked'));
      return Promise.reject(error);
    }

    // Backend-unavailable means we genuinely couldn't reach the backend —
    // network error, DNS failure, connection refused, timeout. A 5xx
    // response means the backend received the request and ran code on it;
    // that's an application bug, not a connectivity problem, so we don't
    // flip the maintenance screen on those. A 4xx is also a reachable
    // backend.
    const isNetworkFailure = !error.response;
    if (isNetworkFailure) {
      // Only flip the maintenance screen for authenticated sessions. Pre-auth
      // requests (Cognito, login bootstrap, autofill re-renders, public IP
      // lookup) flap for reasons unrelated to backend health and were the
      // main source of false positives.
      const isAuthenticated = !!localStorage.getItem('intellasure_user_id');
      if (!isAuthenticated) {
        return Promise.reject(error);
      }

      const now = Date.now();
      // Decay the streak if we haven't seen a failure recently — prevents
      // two unrelated transient failures (e.g. one at auth expiry, one
      // 10 minutes later on autofill) from tripping the screen together.
      if (now - lastFailureAt > FAILURE_DECAY_MS) {
        consecutiveBackendFailures = 0;
      }
      lastFailureAt = now;
      consecutiveBackendFailures += 1;
      if (consecutiveBackendFailures >= FAILURE_THRESHOLD && !backendDownNotified) {
        backendDownNotified = true;
        window.dispatchEvent(new CustomEvent('backend-unavailable'));
      }
    } else {
      // Any response (4xx, 5xx) means the backend is reachable — clear any
      // pending down-state. 5xx is an application error to surface in the
      // affected component, not a global "site is down" signal.
      if (backendDownNotified) {
        backendDownNotified = false;
        window.dispatchEvent(new CustomEvent('backend-available'));
      }
      consecutiveBackendFailures = 0;
    }
    return Promise.reject(error);
  }
);

// the main provider
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate()

  const [email, setEmail] = useState<string>(() => sessionStorage.getItem('reset_email') || '');

  const [currentUser, setCurrentUser] = useState<Account>({
    userId: null,
    username: null,
  });

  const [currentProfile, setCurrentProfile] = useState<Profile>({
    company: null,
    creation_date: null,
    department: null,
    email: null,
    last_login: null,
    first_name: null,
    last_name: null,
    name: null,
    privileges: null,
    userid: null,
  });
  
  const [accessCode, setAccessCode] = useState<accessCode>({
    code: '',
    company: '',
    privileges: '',
    department: ''
  });

  const [authLoading, setAuthLoading] = useState(false);
  const [validLogin, setValidLogin] = useState<boolean>(true);
  const [validAccessCode, setValidAccessCode] = useState<boolean>(false);
  const [invalidEmail, setInvalidEmail] = useState<boolean>(false)
  const [createdAccount, setCreatedAccount] = useState<boolean>(false)
  const [confirmedEmail, setConfirmedEmail] = useState<boolean>(false)
  const [successfulReset, setSuccessfulReset] = useState<boolean>(true)
  const [resetError, setResetError] = useState<string>('')

  const [allUsers, setAllUsers] = useState<ProfileProps[] | null>(null);
  const [intakeUsers, setIntakeUsers] = useState<ProfileProps[] | null>(null);
  const [adminUsers, setAdminUsers] = useState<ProfileProps[] | null>(null);
  const [claimsUsers, setClaimsUsers] = useState<ProfileProps[] | null>(null);
  const [followupUsers, setFollowupUsers] = useState<ProfileProps[] | null>(null);
  const [devUsers, setDevUsers] = useState<ProfileProps[] | null>(null);

  const handleEmailUpdate = (data: string) => {
    setEmail(data)
    sessionStorage.setItem('reset_email', data)
  }

  async function getUserIp() {
    try {
      const response = await axios.get('https://api.ipify.org?format=json');
      return response.data.ip;
    } catch (error) {
      console.error('Error fetching user IP address:', error);
      return null;
    }
  }

  const signInUser = (userInfo: UserDetails) => {
    const { username, password } = userInfo;
  
    setAuthLoading(true);
  
    signIn({ username, password })
      .then((user) => {
        getUserIp()
          .then((clientIp) => {
            if (clientIp) {
              updateUserAttributes({
                userAttributes: {
                  'custom:clientIp': clientIp
                }
              })
              .then(() => {
                console.log('IP address updated successfully');
              })
              .catch((updateError) => {
                console.error('Error updating IP address:', updateError);
              });
            }
          })
          .catch((ipError) => {
            console.error('Error fetching IP address:', ipError);
          });
  
        setValidLogin(true);
        sessionStorage.removeItem('reset_email');
        grabCurrentUser();
      })
      .catch((error) => {
        console.error('Sign-in error:', error);
        setValidLogin(false); // Set validLogin state to false
        setAuthLoading(false);
      });
  };

  const grabCurrentUser = () => {
    setAuthLoading(true);
    getCurrentUser()
      .then((response) => {
        setCurrentUser(response);
        localStorage.setItem('intellasure_user_id', response.userId);
        grabCurrentUserProfile(response.userId)
        navigate('/')
      })
      .catch((error) => {
        console.log(error)
        // Cognito has no current user — clear stale auth ID so the global
        // axios interceptor doesn't treat this session as authenticated and
        // mis-trigger the maintenance screen on pre-auth request failures.
        localStorage.removeItem('intellasure_user_id');
        setAuthLoading(false);
      });
  };

  const grabCurrentUserProfile = (userId: string) => {
    let config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${baseUrl}/users/${userId}`,
      headers: {},
    };
    axios
      .request(config)
      .then((response) => {
        setCurrentProfile(response.data.data);
        setAuthLoading(false);
        // Update last login timestamp
        axios.post(`${baseUrl}/users/${userId}/heartbeat`).catch(() => {});
      })
      .catch((error) => {
        console.log(error)
        setAuthLoading(false);
      });
  };

  const validateAccessCode = (accessCode: string) => {
    if(accessOptions[accessCode as keyof typeof accessOptions]){
      setAccessCode(accessOptions[accessCode as keyof typeof accessOptions])
      setValidAccessCode(true)
    } 
  }

  const removeAccessCode = () => {
    setValidAccessCode(false)
  }

  const createNewUser = (userPassthrough: UserPassthrough) => {
    setEmail(userPassthrough.email)
    const signupData = {
      username: userPassthrough.email,
      email: userPassthrough.email,
      password: userPassthrough.password,
      first_name: userPassthrough.firstName,
      last_name: userPassthrough.lastName,
      full_name: `${userPassthrough.firstName} ${userPassthrough.lastName}`,
      options: {
        userAttributes: {
          email: userPassthrough.email,
          given_name: userPassthrough.firstName,
          family_name: userPassthrough.lastName,
          nickname: userPassthrough.firstName,
          name: `${userPassthrough.firstName} ${userPassthrough.lastName}`
        }
      }
    }

    signUp(signupData)
      .then((currentUser) => {
        setCurrentUser({userId: currentUser.userId, username: currentUser.userId})
        createProfile(currentUser.userId, userPassthrough)
      })
      .catch((err) => {
        if(err.name === 'UsernameExistsException'){
          setInvalidEmail(true)
        }
      });
  }

  const createProfile = (userId: string | undefined, userPassthrough: UserPassthrough) => {
    let data = {
      "first_name": userPassthrough.firstName,
      "last_name": userPassthrough.lastName,
      "name": `${userPassthrough.firstName} ${userPassthrough.lastName}`,
      "email": userPassthrough.email,
      "status": 'active',
      "privileges": accessCode.privileges,
      "department": accessCode.department,
      "company": accessCode.company,
      "user_id": userId
    };

    let config = {
      method: 'put',
      maxBodyLength: Infinity,
      url: `${baseUrl}/users/${userId}`,
      headers: { 
        'Content-Type': 'application/json'
      },
      data : data
    };

    axios.request(config)
      .then((response) => {

        navigate('/auth/confirmation')
      })
      .catch((error) => {
        console.log(error);
      });
  }

  const confirmEmailCode = (confirmationCode: string) => {
    confirmSignUp({
        username: email,
        confirmationCode: confirmationCode
    })
    .then(response => {
      navigate('/auth/login')
    })
    .catch(error => {
        console.log('Error confirming sign up', error);
    });
  };

  const resetConfirmEmail = () => {
    resendSignUpCode({
      username: email
    })
    .then(response => {
    })
    .catch(error => {
        console.log('Error confirming sign up', error);
    });
  }

  const confirmResetPasswrd = (
    confirmationCode: string,
    newPassword: string,
  ) => {
    setResetError('')
    confirmResetPassword({
      username: email, confirmationCode, newPassword
    })
    .then(() => {
      sessionStorage.removeItem('reset_email')
      navigate('/auth/login')
    }).catch((error) => {
      setSuccessfulReset(false)
      const msg = error?.message || 'Password reset failed. Please try again.'
      setResetError(msg)
      console.log(error)
    })
  }

  const resetUserEmail = (email: string) => {
    setEmail(email)
    sessionStorage.setItem('reset_email', email)
    resetPassword({username: email})
      .then((response) => {
        navigate('/auth/reset-password')
      })
      .catch((error) => {
        console.log(error)
      })
  }

  const signOutUser = () => {
    signOut()
      .then((response) => {
        setCurrentUser({ userId: null, username: null })
        localStorage.removeItem('intellasure_user_id');
        navigate('/auth/login')
      })
      .catch((error) => {
        console.log(error)
        setValidLogin(false)
      })
  }

  const unvalidateAccessCode = () => {
    setValidAccessCode(false)
  }

  const grabAllProfiles = () => {
    axios.get<ProfileProps[]>(`${baseUrl}/users/all`)
      .then((response) => {
        let intake: any = []
        let claims: any = []
        let followup: any = []
        let admin: any = []
        let dev: any = []
        setAllUsers(response.data);
        response.data.map((singleUser) => {
          // Only include active users in department arrays for UI functionality
          if(singleUser.active) {
            if(singleUser.department === 'intake'){
              intake.push(singleUser)
            }
            if(singleUser.department === 'bd'){
              intake.push(singleUser) // BD users have manager privileges in intake department
            }
            if(singleUser.department === 'claims'){
              claims.push(singleUser)
            }
            if(singleUser.department === 'admin'){
              admin.push(singleUser)
            }
            if(singleUser.department === 'dev'){
              dev.push(singleUser)
            } 
            if(singleUser.department === 'followup'){
              followup.push(singleUser)
            }
          }
        })
        setAdminUsers(admin)
        setIntakeUsers(intake)
        setClaimsUsers(claims)
        setFollowupUsers(followup)
        setDevUsers(dev)
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        currentProfile,
        authLoading,
        validLogin,
        validAccessCode,
        invalidEmail,
        createdAccount,
        confirmedEmail,
        accessCode,
        successfulReset,
        resetError,
        email,
        allUsers,
        intakeUsers,
        adminUsers,
        claimsUsers,
        followupUsers,
        devUsers,
        signInUser,
        createNewUser,
        removeAccessCode,
        validateAccessCode,
        signOutUser,
        unvalidateAccessCode,
        grabCurrentUser,
        confirmEmailCode,
        resetConfirmEmail,
        grabCurrentUserProfile,
        resetUserEmail,
        confirmResetPasswrd,
        handleEmailUpdate,
        grabAllProfiles
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
